import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  findAllProduct,
  findAllActiveProduct,
  insertProduct,
  updateProduct,
  deleteProduct,
  findByIDProduct,
  activeInActiveProduct,
  productImagesUpdate,
  deleteProductImage,
  findByURLProduct,
} from "./product.service";
import { IProduct, IProductState } from "./productModel";

const initialState: IProductState = {
  loading: false,
  error: "",
  ProductList: [],
  ProductID: null,
  ProductDetail: {
    ID: 0,
    ProductName: "",
    URL: "",
    ShortDescription: "",
    Price: 0,
    PurchasePrice: 0,
    SKUNO: "",
    Qty: 0,
    CategoryID: 0,
    IsActive: true,
    LongDescription: "",
    AmazonLink: "",
    MeeshoLink: "",
    FlipkartLink: "",
    YoutubeVideoLink: "",
    xLink: "",
    MetaTitle: "",
    MetaKeyword: "",
    MetaDescription: "",
    MainImage: "",
    InnerImages: [],
    ProductPrices: [],
    GSTPer: 3,
    IsHomeDisplay: false,
  },
  isDeleteModalOpen: false,
  DeleteProductID: null,
  DeleteImageID: null,
  totalCount: 0,
  FilterProduct: {
    ProductName: "",
    CategoryName: "",
    Price: null,
    PurchasePrice: null,
    SKUNO: "",
    Qty: "",
    CategoryID: "",
  },
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    clearProductDetails: (state) => {
      state.ProductDetail = initialState.ProductDetail;
    },
    setProductID: (state, action: PayloadAction<number>) => {
      state.ProductID = action.payload;
    },
    clearProductID: (state) => {
      state.ProductID = null;
    },
    openDeleteModal: (state, action: PayloadAction<number>) => {
      state.DeleteProductID = action.payload;
      state.isDeleteModalOpen = true;
    },
    closeDeleteModal: (state) => {
      state.isDeleteModalOpen = false;
      state.DeleteProductID = null;
    },
    setDeleteImageID: (state, action: PayloadAction<number>) => {
      state.DeleteImageID = action.payload;
    },
    setFilterProduct: (state, action: PayloadAction<any>) => {
      state.FilterProduct = action.payload;
    },
    clearFilterProduct: (state) => {
      state.FilterProduct = initialState.FilterProduct;
    },
  },
  extraReducers: (builder) => {
    // Find All
    builder.addCase(findAllProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      findAllProduct.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.ProductList = action.payload?.Data || [];
        state.totalCount =
          action.payload?.Data[0]?.ItemCount ||
          action.payload?.Data.length ||
          0;
        state.error = "";
      },
    );
    builder.addCase(findAllProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ProductList = [];
      state.totalCount = 0;
    });

    // Find All Active
    builder.addCase(findAllActiveProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      findAllActiveProduct.fulfilled,
      (state, action: PayloadAction<IProduct[]>) => {
        state.loading = false;
        state.ProductList = action.payload || [];
        state.totalCount = action.payload.length || 0;
        state.error = "";
      },
    );
    builder.addCase(findAllActiveProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ProductList = [];
      state.totalCount = 0;
    });

    // Find By ID
    builder.addCase(findByIDProduct.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      findByIDProduct.fulfilled,
      (state, action: PayloadAction<IProduct>) => {
        state.loading = false;
        state.ProductDetail = action.payload;
      },
    );
    builder.addCase(findByIDProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Find By URL
    builder.addCase(findByURLProduct.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      findByURLProduct.fulfilled,
      (state, action: PayloadAction<IProduct>) => {
        state.loading = false;
        state.ProductDetail = action.payload;
      },
    );
    builder.addCase(findByURLProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // Insert/Update/Delete Pending
    builder.addMatcher(
      (action) =>
        [
          insertProduct.pending,
          updateProduct.pending,
          deleteProduct.pending,
          activeInActiveProduct.pending,
          productImagesUpdate.pending,
          deleteProductImage.pending,
        ].includes(action.type),
      (state) => {
        state.loading = true;
        state.error = "";
      },
    );

    // Insert/Update/Delete Fulfilled
    builder.addMatcher(
      (action) =>
        [
          insertProduct.fulfilled,
          updateProduct.fulfilled,
          deleteProduct.fulfilled,
          activeInActiveProduct.fulfilled,
          productImagesUpdate.fulfilled,
          deleteProductImage.fulfilled,
        ].includes(action.type),
      (state) => {
        state.loading = false;
      },
    );

    // Insert/Update/Delete Rejected
    builder.addMatcher(
      (action) =>
        [
          insertProduct.rejected,
          updateProduct.rejected,
          deleteProduct.rejected,
          activeInActiveProduct.rejected,
          productImagesUpdate.rejected,
          deleteProductImage.rejected,
        ].includes(action.type),
      (state, action: any) => {
        state.loading = false;
        state.error = action.error?.message || "Something went wrong";
      },
    );
  },
});

export const {
  clearProductDetails,
  setProductID,
  clearProductID,
  openDeleteModal,
  closeDeleteModal,
  setDeleteImageID,
  setFilterProduct,
  clearFilterProduct,
} = productSlice.actions;

export default productSlice.reducer;
